/**
 * Frontend-resident reset configuration for the motor wizard.
 *
 * These lists are injected into the wizard config at format time (see formatForm
 * in ./formatWizard.js), which re-runs on every render — so they take effect even
 * against a stale redux-persisted `wizard` config, without any Strapi change and
 * without putting motor-specific logic in the reducer.
 *
 * Each entry is `{ name, staticValue }`, consumed by the generic
 * `resetWizardFieldsOnChange` action (RESET_WIZARD_FIELDS_ONCHANGE), which sets
 * each field to its staticValue.
 */

// GgilCheckChoice name of the EV Battery benefit (note the trailing space — it
// matches the wizard config exactly and is the key the checkbox reads/writes).
const EV_BATTERY_CHECK_KEY = 'Electric Vehicle Battery Replacement or Repair ';

// Top-level "Optional Extra Benefits" checkbox keys. Each GgilCheckChoice stores
// its selection as an array, so the "unchecked" state is an empty array; some also
// persist a `${name}Payload` companion that must be nulled.
const MOTOR_OPTIONAL_BENEFIT_KEYS = [
  'waiverOfExcess',
  'alternativeVehicleAllowance',
  'additionalWindscreenCover',
  'hospitalizationBenefit',
  'survivorsBenefit',
  'PersonalAccidentBenefit',
  'Motor Assist (Road Side Assistance)',
  'Wind Screen-All Glass Cover',
  'Mechanical Benefit',
  EV_BATTERY_CHECK_KEY,
  'Agreed Depreciation',
];

// Dependent sub-fields rendered inside each benefit's sub-form, cleared to ''.
const MOTOR_OPTIONAL_BENEFIT_DEPENDENT_KEYS = [
  'waiverAppliesTo',
  'additionalWindscreenCoverAmount',
  'hospitalizationHeight',
  'hospitalizationWeight',
  // Physician inputs write under `hospitalizationPhysician*`; `personalPhysician*`
  // is only the reducerKeyValue. Reset both to actually clear the typed value.
  'hospitalizationPhysicianName',
  'hospitalizationPhysicianAddress',
  'personalPhysicianName',
  'personalPhysicianAddress',
  'hospitalizationRespiratoryCondition',
  'hospitalizationHeartCondition',
  'hospitalizationKidneyCondition',
  'hospitalizationStomachCondition',
  'hospitalizationMuscleCondition',
  'hospitalizationEyesCondition',
  'hospitalizationOtherCondition',
  'hospitalizationOtherCoverage',
  'hospitalizationOtherCoverageCompany',
  'hospitalizationOtherCoverageGroup',
  'hospitalizationDeclaration',
  'survivorBeneficiaryAgeGroup',
  'survivorBeneficiariesCount',
  'survivorBeneficiary1FullName',
  'survivorBeneficiary1Dob',
  'survivorBeneficiary1Relationship',
  'survivorBeneficiary2FullName',
  'survivorBeneficiary2Dob',
  'survivorBeneficiary2Relationship',
  'survivorBeneficiary3FullName',
  'survivorBeneficiary3Dob',
  'survivorBeneficiary3Relationship',
  'survivorBeneficiaryDeclaration',
  'windscreenCoverValue',
  'mechanicalBenefitValue',
  'electricVehicleBatteryOption',
];

// Sub-form fields owned by each optional benefit, keyed by the benefit's
// GgilCheckChoice `name`. Unchecking a benefit must clear ITS OWN sub-form so
// stale answers (e.g. Personal Physician Name/Address) don't persist in the
// store — otherwise they leak into the submission payload and can make a
// re-checked benefit look "completed" against isMotorBenefitComplete.
const MOTOR_BENEFIT_DEPENDENT_KEYS_BY_NAME = {
  waiverOfExcess: ['waiverAppliesTo'],
  additionalWindscreenCover: ['additionalWindscreenCoverAmount'],
  hospitalizationBenefit: [
    'hospitalizationHeight',
    'hospitalizationWeight',
    // The Personal Physician inputs write under `hospitalizationPhysician*` (the
    // GgilValueInput `name`); `personalPhysician*` is only the reducerKeyValue.
    // Reset both so the typed value is actually cleared.
    'hospitalizationPhysicianName',
    'hospitalizationPhysicianAddress',
    'personalPhysicianName',
    'personalPhysicianAddress',
    'hospitalizationRespiratoryCondition',
    'hospitalizationHeartCondition',
    'hospitalizationKidneyCondition',
    'hospitalizationStomachCondition',
    'hospitalizationMuscleCondition',
    'hospitalizationEyesCondition',
    'hospitalizationOtherCondition',
    'hospitalizationOtherCoverage',
    'hospitalizationOtherCoverageCompany',
    'hospitalizationOtherCoverageGroup',
    'hospitalizationDeclaration',
  ],
  survivorsBenefit: [
    'survivorBeneficiaryAgeGroup',
    'survivorBeneficiariesCount',
    'survivorBeneficiary1FullName',
    'survivorBeneficiary1Dob',
    'survivorBeneficiary1Relationship',
    'survivorBeneficiary2FullName',
    'survivorBeneficiary2Dob',
    'survivorBeneficiary2Relationship',
    'survivorBeneficiary3FullName',
    'survivorBeneficiary3Dob',
    'survivorBeneficiary3Relationship',
    'survivorBeneficiaryDeclaration',
  ],
  'Wind Screen-All Glass Cover': ['windscreenCoverValue'],
  'Mechanical Benefit': ['mechanicalBenefitValue'],
  [EV_BATTERY_CHECK_KEY]: ['electricVehicleBatteryOption'],
};

// Per-benefit resetFields (each dependent sub-field cleared to ''), keyed by the
// benefit checkbox `name`. Wired in formatWizard.js so unchecking a single
// benefit clears only that benefit's sub-form.
export const MOTOR_BENEFIT_RESET_FIELDS_BY_NAME = Object.fromEntries(
  Object.entries(MOTOR_BENEFIT_DEPENDENT_KEYS_BY_NAME).map(([name, fields]) => [
    name,
    fields.map(field => ({ name: field, staticValue: '' })),
  ]),
);

// resetFields for the coverage-plan selector (MotorExpansionPanelChoice): clears
// every optional benefit + its payload + its dependent sub-fields on plan change.
export const MOTOR_COVER_TYPE_RESET_FIELDS = [
  ...MOTOR_OPTIONAL_BENEFIT_KEYS.flatMap(name => [
    { name, staticValue: [] },
    { name: `${name}Payload`, staticValue: null },
  ]),
  ...MOTOR_OPTIONAL_BENEFIT_DEPENDENT_KEYS.map(name => ({ name, staticValue: '' })),
];

// resetFields for the "young drivers" question (youngDrivers): changing this
// answer changes the eligible benefits/premium on the coverage plan, so clear the
// same optional benefits as a plan change. Without this, benefits selected before
// toggling the answer (e.g. No -> Yes) stay checked on the coverage-plan page.
export const MOTOR_YOUNG_DRIVERS_RESET_FIELDS = MOTOR_COVER_TYPE_RESET_FIELDS;

// Step 2 rating inputs (config component `name`s) that change the premium and the
// eligible plans/benefits. Changing any of them after a plan + optional benefits
// were selected (e.g. go Back from the Coverage Plan step, change No. of Seats,
// continue) must clear the optional benefits exactly like a plan change — only the
// EV Battery benefit cleared itself before (via the reducer eligibility logic),
// leaving the rest checked against a now-stale premium. These mirror the
// PREMIUM_INPUT_FIELD_ALIASES the reducer uses to detect a premium-input change.
// Wired in formatWizard.js to MOTOR_COVER_TYPE_RESET_FIELDS, the same reset list
// the coverage-plan selector and youngDrivers use.
export const MOTOR_PREMIUM_INPUT_RESET_FIELD_NAMES = [
  'NumberofSeats',
  'vehicleCoverageType',
  'vehicleFuelType',
  'vehicleBodyType',
  'vehicleType',
  'vehicleUsage',
  'vehicleYear',
  'vehicleValue',
  'CubicCapacityRating',
  'vehicleClaimsHistory',
  'noClaimPeriodDuration',
  'vehicleModified',
  'vehicleParkedArea',
  'existingMotorInsurance',
];

// resetFields for the "Do you have an Agent?" field (hasAgent): clears the agent
// dropdown selection, its companion values and the "other agent" free-text.
export const MOTOR_HAS_AGENT_RESET_FIELDS = [
  { name: 'stateAgent', staticValue: '' },
  { name: 'stateAgentLabel', staticValue: '' },
  { name: 'stateAgentPayload', staticValue: null },
  { name: 'stateAgentOther', staticValue: '' },
];
